// frontend/src/pages/SharedProject.js

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Tooltip,
  Avatar,
  Button,
  Stack,
  Checkbox,
  Snackbar
} from '@mui/material';
import {
  Launch as OpenIcon,
  Bookmark as BookmarkIcon,
  Public as PublicIcon,
  Error as ErrorIcon,
  Notes as NotesIcon,
  Extension as ExtensionIcon
} from '@mui/icons-material';
import axios from '../api/axiosConfig';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { ProjectsAPI } from '../api/projectsV2';
import { detectBrowser, getExtensionUrl, getBrowserLabel } from '../config/extensionUrls';

const slugify = (text) =>
  (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

const SharedProject = () => {
  const theme = useTheme();
  const { token, slug } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [project, setProject] = useState(null);
  const [owner, setOwner] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forking, setForking] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchSharedProject = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/shared/project/${token}`);
        setProject(response.data.project);
        setOwner(response.data.owner || null);
        setBookmarks(response.data.bookmarks || []);
        setNotes(response.data.notes || []);
        setTasks(response.data.tasks || []);
      } catch (error) {
        console.error('Error fetching shared project:', error);
        setError(error.response?.data?.error || 'Failed to load shared project');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedProject();
    }
  }, [token]);

  // Pretty URL slug via replaceState
  useEffect(() => {
    if (!project?.name || slug) return;
    const nameSlug = slugify(project.name);
    if (!nameSlug) return;
    window.history.replaceState(null, '', `/shared/project/${token}/${nameSlug}`);
  }, [project, token, slug]);

  const forkProject = async () => {
    if (!currentUser) {
      navigate('/login?next=' + encodeURIComponent(window.location.pathname));
      return;
    }

    setForking(true);
    try {
      // Create a new project with same name + copy
      const name = `${project.name} (copy)`;
      const { data: created } = await ProjectsAPI.create({
        name,
        color: project.color || '#1976d2',
        visibility: 'private'
      });
      const newProject = created.project || created;

      // Copy note if present (v2 shares have virtual note)
      const note = (notes[0]?.text || '').trim();
      if (note) {
        await ProjectsAPI.putNote(newProject._id, note);
      }

      // Copy tasks (v2 shares only)
      if (tasks.length) {
        for (const t of tasks) {
          await ProjectsAPI.createTask(newProject._id, {
            title: t.title,
            status: t.status,
            dueAt: t.dueAt || null
          });
        }
      }

      setSnackbar({ open: true, message: 'project_saved', severity: 'success' });
      setTimeout(() => navigate(`/projects/${newProject._id}`), 1500);
    } catch (error) {
      console.error('Failed to fork project:', error);
      setSnackbar({ open: true, message: 'Failed to save project', severity: 'error' });
    } finally {
      setForking(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" icon={<ErrorIcon />}>
          {error}
        </Alert>
      </Container>
    );
  }

  const projectColor = project?.color || theme.palette.success.main;

  const seoTitle = project?.name ? `${project.name} — Blackmount` : 'Shared Project — Blackmount';
  const seoDescription = project?.description || `A shared project on Blackmount${owner?.username ? ` by @${owner.username}` : ''}`;
  const seoCanonical = `https://app.blackmount.ai/shared/project/${token}${project?.name ? '/' + slugify(project.name) : ''}`;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoCanonical} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoCanonical} />
        <meta property="og:image" content="https://app.blackmount.ai/og-image.png" />
        <meta property="og:site_name" content="Blackmount" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://app.blackmount.ai/og-image.png" />
      </Helmet>

      {/* Project Header */}
      <Card sx={{ mb: 3, border: `2px solid ${projectColor}` }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: projectColor }} />
              <Typography variant="h4" fontWeight={700} sx={{ color: projectColor }}>
                {project?.name || 'Shared Project'}
              </Typography>
              <Chip
                label={project?.isPublic ? 'Public Project' : 'Shared'}
                size="small"
                color="success"
                variant="outlined"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              {currentUser ? (
                <Button
                  variant="contained"
                  onClick={forkProject}
                  disabled={forking}
                >
                  {forking ? 'Saving...' : 'Save to my Blackmount'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => navigate('/login?next=' + encodeURIComponent(window.location.pathname))}
                >
                  Sign up to save
                </Button>
              )}
            </Stack>
          </Stack>

          {project?.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {project.description}
            </Typography>
          )}

          {/* Owner Attribution */}
          {owner ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 2,
              p: 1.25,
              bgcolor: 'action.hover',
              borderRadius: 1
            }}>
              <Avatar
                src={owner.profilePictureUrl}
                alt={owner.displayName}
                sx={{ width: 32, height: 32 }}
              />
              <Typography variant="body2" color="text.secondary">
                Curated by{' '}
                <Link
                  to={`/u/${owner.username}`}
                  style={{ fontWeight: 'bold', color: theme.palette.primary.main, textDecoration: 'none' }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                  @{owner.username}
                </Link>
              </Typography>
              {owner.joinDate && (
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  Member since {new Date(owner.joinDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Curated on Blackmount
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {tasks.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {bookmarks.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BookmarkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {bookmarks.length} bookmark{bookmarks.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {notes.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {notes.length} note{notes.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {project?.createdAt && (
              <Typography variant="body2" color="text.secondary">
                Created {formatDate(project.createdAt)}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Project Content */}
      <Paper sx={{ borderRadius: 2 }}>
        {bookmarks.length === 0 && notes.length === 0 && tasks.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <BookmarkIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Content in This Project
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This project doesn't contain any content yet.
            </Typography>
          </Box>
        ) : (
          <List>
            {/* Notes Section (v2 virtual note or v1 notes) */}
            {notes.length > 0 && notes.map((note, index) => (
              <ListItem 
                key={`note-${note._id}`} 
                divider={index < notes.length - 1 || bookmarks.length > 0}
                sx={{ py: 2 }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotesIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="subtitle1" fontWeight={500}>
                        {note.title || 'Untitled Note'}
                      </Typography>
                      <Chip 
                        label="Note" 
                        size="small" 
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {note.preview}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created {formatDate(note.createdAt)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}

            {/* Tasks Section (v2 shared projects) */}
            {tasks.length > 0 && tasks.map((task, index) => (
              <ListItem
                key={`task-${task._id}`}
                divider={index < tasks.length - 1 || bookmarks.length > 0}
                sx={{ py: 2 }}
              >
                <Checkbox
                  checked={task.status === 'done'}
                  disabled
                  sx={{ mr: 1 }}
                />
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={500}
                        sx={{
                          textDecoration: task.status === 'done' ? 'line-through' : 'none',
                          opacity: task.status === 'done' ? 0.6 : 1
                        }}
                      >
                        {task.title}
                      </Typography>
                      <Chip
                        label={task.status}
                        size="small"
                        color={task.status === 'done' ? 'success' : task.status === 'doing' ? 'primary' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    task.dueAt ? (
                      <Typography variant="caption" color="text.secondary">
                        Due: {new Date(task.dueAt).toLocaleDateString()}
                      </Typography>
                    ) : undefined
                  }
                />
              </ListItem>
            ))}

            {/* Bookmarks Section (v1 legacy) */}
            {bookmarks.map((bookmark, index) => (
              <ListItem 
                key={bookmark._id} 
                divider={index < bookmarks.length - 1}
                disablePadding
              >
                <ListItemButton
                  component="a"
                  href={bookmark.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ py: 2 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {bookmark.favicon && (
                          <img 
                            src={bookmark.favicon} 
                            alt="" 
                            style={{ width: 16, height: 16 }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <Typography variant="subtitle1" fontWeight={500}>
                          {bookmark.title}
                        </Typography>
                        <Chip 
                          label="Bookmark" 
                          size="small" 
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {bookmark.url}
                        </Typography>
                        {bookmark.description && (
                          <Typography variant="body2" color="text.secondary">
                            {bookmark.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {bookmark.hostname && (
                            <Chip 
                              label={bookmark.hostname} 
                              size="small" 
                              variant="outlined" 
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {bookmark.contentType && (
                            <Chip 
                              label={bookmark.contentType} 
                              size="small" 
                              variant="outlined" 
                              color="primary"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Open bookmark">
                      <IconButton
                        edge="end"
                        onClick={() => window.open(bookmark.url, '_blank')}
                        color="primary"
                      >
                        <OpenIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Footer */}
      <Box sx={{ mt: 5, textAlign: 'center', pb: 2 }}>
        {/* Extension install nudge */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <ExtensionIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Save your own browser sessions in one click
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          href={getExtensionUrl(detectBrowser())}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<ExtensionIcon />}
          sx={{ mb: 3, textTransform: 'none' }}
        >
          Install for {getBrowserLabel(detectBrowser())}
        </Button>

        <Typography variant="body2" color="text.secondary">
          Powered by Blackmount &bull; <a href="/" style={{ color: 'inherit' }}>Create your own projects</a>
        </Typography>
      </Box>

      {/* Snackbar for fork feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message === 'project_saved' ? (
            <>
              Project saved!{' '}
              <a
                href={getExtensionUrl(detectBrowser())}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                Install the extension
              </a>{' '}
              to save your own browser tabs
            </>
          ) : (
            snackbar.message
          )}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SharedProject;